// ─────────────────────────────────────────────
//  bench — who is on shift and in no room, and why they are not spare.
//
//  ⚠️ MOVED OUT OF useControlPanelData ON PURPOSE. It was a pure function
//  parked at module scope in a hook file, with a note saying it belonged in
//  `domain/` and could not go in `domain/controlPanel.js` because that file is
//  already over the 500-line ceiling. The MOBILE break board then needed the
//  same derivation, and importing it from the hook would have dragged
//  AlertContext and firebase/firestore into the lazy mobile chunk for one pure
//  helper. It gets its own domain file instead — the option the original note
//  was denied.
//
//  `useControlPanelData` imports AND re-exports it, so every existing
//  `import { benchProviders } from ".../useControlPanelData"` keeps working.
// ─────────────────────────────────────────────
import { isCaselessWindow, rosterCrnaDepartMin, rowClaimsAt } from "./timeline";
import { orientationOf } from "./orientation";

// Providers on today's roster who are on shift and in no room. `departed` is a
// map keyed by member id; an assignment row claims a person via `ahpId`.
//
// ⚠️ A ROW WITH NO CASE WINDOW DOES NOT CLAIM ANYBODY. The OB on-call CRNA has
// an assignment row all day (that row is what the "OB must always be covered"
// rule is judged against — domain/coverage.js — so it must never be deleted),
// but with no case on it they are FREE: free to cover an epidural or a section,
// and free to be called. Matching on `ahpId` alone hid them from FREE NOW and
// from the board's Unassigned list for the entire shift, which is precisely the
// invisibility BenchSections was written to prevent.
//
// The test is `isCaselessWindow`, the SAME predicate the chart uses to decide it
// draws no bar. One predicate, so the chart and the bench cannot disagree about
// whether this person is busy. ⚠️ NOT `isStandingOnCall`, which is that AND the
// `isOnCall` flag: being released by an empty row and being on call are two
// different facts, and reading one through the other is what let a "9" with no
// cases be shown as a 24-hour watch (2026-09-13).
//
// The returned member carries `onCall` + `onCallRoom` — only when the row is
// genuinely flagged on call — so the views render a label instead of
// re-deriving the fact from assignments a second time.
export function benchProviders(roster, assignments = [], nowMin = null) {
  const crnas = (roster?.crnas || []).filter((c) => c && c.id);
  const departed = roster?.departed || {};
  // GIVING BREAKS rides in the same way `onCall` does: set here, on the ONE
  // bench derivation, so FREE NOW, the board's Unassigned list and the mobile
  // break board cannot disagree about who is doing the break rounds. The person
  // stays in the pool — that is the whole point of the label — so this changes
  // no filter, only what the row says.
  const givingBreaks = roster?.givingBreaks || {};
  const assigned = new Set();
  const watchByAhp = new Map();
  for (const a of Array.isArray(assignments) ? assignments : []) {
    if (!a) continue;
    // ⚠️ AN ORIENTEE IS NOT FREE. They are standing in the preceptor's room for
    // the whole case. Left out of this set the board offered them as available
    // and a charge nurse could be sent looking for somebody who is already in a
    // room — the mirror image of the invisibility this list exists to prevent.
    // They are still NOT the seat: the ratio counts the preceptor, never them.
    // ⚠️ A FINISHED CASE RELEASES EVERYONE ON IT. `rowClaimsAt` is the owner's
    // 2026-08-24 rule: once the room has no more cases, the provider is free.
    // Read BEFORE the orientee line so the trainee is released with their
    // preceptor — they left the room together.
    if (!rowClaimsAt(a, nowMin)) continue;
    const orienting = orientationOf(a);
    if (orienting?.orienteeId) assigned.add(orienting.orienteeId);
    if (!a.ahpId) continue;
    // ⚠️ A ROW WITH NO CASE WINDOW DOES NOT OCCUPY ANYBODY — whether or not it
    // is a call watch. This asked `isStandingOnCall`, which is `isOnCall AND
    // caseless`, so the release depended on a flag that says something else
    // entirely. It only ever worked because the importer set `isOnCall` on
    // every timeless row; the moment that flag narrowed to real call codes, a
    // "9" in a room with no cases would have been pinned there all day.
    //
    // The room having no cases is the owner's own 2026-08-24 rule — "as soon as
    // there are no more cases in the room, the providers are released to free
    // now" — and a caseless row is that rule's limit case: there were never any.
    if (isCaselessWindow(a)) {
      if (!watchByAhp.has(a.ahpId)) watchByAhp.set(a.ahpId, a);
      continue;
    }
    assigned.add(a.ahpId);
  }
  return crnas
    .filter((c) => !departed[c.id] && !assigned.has(c.id))
    .map((c) => {
      const watch = watchByAhp.get(c.id);
      // ⚠️ WHEN THIS PERSON IS DUE TO LEAVE, AND WHETHER THAT HOUR HAS PASSED —
      // two separate facts, and neither is a departure. Owner, 2026-09-10: "if
      // you have somebody in the free now list, that they suppost to leave at 3
      // pm, and the time is 3:15 pm, removed from the list and send them home."
      // This FLAGS; it never marks anybody departed. A cover window ending is
      // SCHEDULED, a departure happens at any minute for any reason, and writing
      // one from the other under-counts every CRNA who stays late — the
      // direction that makes the board report itself covered when it is not.
      //
      // ⚠️ `rosterCrnaDepartMin` AND NOT A COMPARISON WRITTEN HERE. An on-call
      // CRNA resolves to 31:00 (1860), deliberately past midnight, so they read
      // as staying at every hour of the working day. A fresh `now > 7*60` would
      // send the OB on-call CRNA home at 7:01 AM on the wrong day.
      const departMin = rosterCrnaDepartMin(c);
      // No clock (a planned board) and no stated hour both mean "not overdue".
      // Absence is not expiry — every row in the reported screenshot had none.
      const dueOut = departMin != null && nowMin != null && Number.isFinite(nowMin)
        ? nowMin > departMin
        : false;
      const withHours = { ...c, departMin: departMin ?? null, dueOut };
      // ⚠️ RELEASED IS NOT ON CALL. Every caseless row releases its person
      // (above); only a row actually FLAGGED on call is a watch, and only a
      // watch may wear the label. Owner, 2026-09-13, on a "9" the board was
      // calling a watch in WOR 1: "titi somade is a 9 … the one on call is jim
      // slone." Somebody released by an empty row is simply free, and the board
      // says nothing further about them.
      const base = watch && watch.isOnCall
        ? { ...withHours, onCall: true, onCallRoom: watch.orRoom || "" }
        : withHours;
      // Both facts can be true at once (the OB watch can be the one walking the
      // break rounds), so this ADDS a flag rather than choosing between them.
      return givingBreaks[c.id] ? { ...base, givingBreaks: true } : base;
    });
}

// The people walking the break rounds. One call, so every surface that lists
// break-givers lists the same people.
//
// ⚠️ HOLDING A ROOM DOES NOT STOP YOU GIVING BREAKS, and restricting this to
// the bench was a real gap: BoardPage has always said so in its own words —
// "a CRNA already in a room can still give a colleague a break" — while the
// label that marks a break-giver could only be set on somebody who was free.
// Owner confirmed 2026-09-07: make it real for assigned staff.
//
// The distinction that survives is WHERE they are, not whether they count:
// an assigned break-giver carries `room`, so the AIC reading the list knows
// they have to be pulled out of somewhere first. A bench break-giver has none.
export function breakGivers(roster, assignments = [], nowMin = null) {
  const flagged = roster?.givingBreaks || {};
  const departed = roster?.departed || {};
  const rows = Array.isArray(assignments) ? assignments : [];

  // Where each flagged person is standing right now, if anywhere. Only rows
  // that STILL CLAIM them count — a finished case releases its provider, so
  // somebody whose room is over is a bench break-giver again, not a busy one.
  const roomByProvider = new Map();
  for (const a of rows) {
    if (!a || !a.ahpId || !rowClaimsAt(a, nowMin)) continue;
    // ⚠️ A STANDING WATCH IS NOT HOLDING THE ROOM, and this loop used to say it
    // was. `benchProviders` skips these rows — that is why the OB on-call CRNA
    // counts as free — and this map did not, so ONE function carried two
    // definitions of "in a room" and contradicted itself on the same person.
    //
    // Live, 2026-09-13, the moment free CRNAs joined this list: Titilope Somade
    // sat on the bench as free AND the cover picker offered her as
    // "Titilope Somade · in WOR 1". Her row is `WOR 1, 07:00 → 07:00, no case,
    // no procedure` — the standing-watch shape. Owner: "what do you mean in
    // room 1". A label that says somebody must be pulled out of a room they are
    // not in is worse than no label; the watch itself is carried below, as a
    // watch. Same predicate as the bench and the chart, so the three cannot
    // disagree about whether this person is busy.
    if (isCaselessWindow(a)) continue;
    if (!roomByProvider.has(a.ahpId)) roomByProvider.set(a.ahpId, a.orRoom || "");
  }

  // ⚠️ FREE COUNTS, NOT ONLY FLAGGED — AND THAT IS THE WHOLE LIST'S MEANING.
  // Reported live 2026-09-13: the cover picker read `No break CRNA free` on a
  // board showing eight unassigned CRNAs with Assign buttons beside them. Every
  // CRNA here was gated on the `givingBreaks` roster flag, and a live read of
  // `daily_boards` found `givingBreaks: {}` on EVERY board of EVERY facility —
  // tampa, default and carrollwood alike. Nobody has ever set it. So this half
  // of the list was structurally empty from the day it shipped, and the picker
  // could not offer a CRNA on any real board, ever.
  //
  // Membership answers "who can be asked". The `givingBreaks` flag, carried on
  // the row, answers the OTHER question — "who is walking the rounds today" —
  // and the GIVING BREAKS strip filters on it. One list, two questions, each
  // surface reading its own; merging them is what produced both symptoms.
  //
  // ⚠️ `benchProviders` DECIDES WHO IS FREE, NOT A SECOND TEST WRITTEN HERE.
  // It is the one bench derivation, and it already knows the rules that a
  // `rowClaimsAt` scan below does not: a standing on-call row does not claim
  // anybody, and an ORIENTEE is claimed even though no row names them as the
  // seat. A local definition would offer the trainee standing in a preceptor's
  // room and hide the OB on-call CRNA who is genuinely free — in both
  // directions, the error that sends somebody to fetch the wrong colleague.
  // The bench entry itself, not just the id: it already knows whether this
  // person is on a standing watch and in which room, and that fact has to reach
  // the picker — "on call WOR 1" is a true thing to say about somebody who is
  // free, where "in WOR 1" is not.
  const benchById = new Map(
    benchProviders(roster, assignments, nowMin).map((p) => [p.id, p])
  );

  // ⚠️ DEPARTED STILL WINS. Somebody who went home is not walking any rounds,
  // whatever label the roster still carries.
  const crnas = (roster?.crnas || [])
    .filter((c) => c && c.id && !departed[c.id] && (flagged[c.id] || benchById.has(c.id)))
    .map((c) => {
      const room = roomByProvider.get(c.id);
      const onBench = benchById.get(c.id) || null;
      // ⚠️ `roleLabel` IS STAMPED HERE, ONCE, AND NEVER GUESSED DOWNSTREAM.
      // The card used to print `(p.subRole || p.role || "crna")`, and a default
      // that ASSERTS a clinical role is a lie waiting for the first person it
      // does not describe — see the doctors below. For a CRNA the fallback is
      // justified by PROVENANCE rather than by a guess: this list is
      // `roster.crnas`, so anyone in it is a CRNA even when the entry carries no
      // role field of its own.
      const roleLabel = String(c.subRole || c.role || "crna").toUpperCase();
      // ⚠️ THE FLAG IS READ, NEVER ASSERTED. This was a hardcoded `true`, which
      // was harmless only while the flag was also the entry condition. Now that
      // a merely-free colleague is on the list, stamping `true` on them would
      // put every free CRNA on the board under a GIVING BREAKS heading and
      // assert a duty nobody assigned them.
      const givingBreaks = !!flagged[c.id];
      // Carried, not re-derived: the standing watch is the reason this person
      // reads as free, and the picker needs to name it rather than imply they
      // are holding the room.
      const watch = onBench?.onCall
        ? { onCall: true, onCallRoom: onBench.onCallRoom || "" }
        : null;
      return room != null
        ? { ...c, givingBreaks, roleLabel, room, assigned: true, ...(watch || {}) }
        : { ...c, givingBreaks, roleLabel, room: "", assigned: false, ...(watch || {}) };
    })
    // The designated break-giver is the ANSWER; a free colleague is the
    // fallback. Stable within each group, so the roster's own order survives.
    .sort((a, b) => Number(b.givingBreaks) - Number(a.givingBreaks));

  // ⚠️ DOCTORS TOO — asked for live on 2026-09-11, with a CRNA's lunch showing
  // red and the bench empty: "we need an option to use doctors in the roster to
  // break". A supervising anaesthesiologist relieving their own CRNA for a meal
  // is ordinary practice, and until now the picker could only ever offer a CRNA
  // carrying the GIVING BREAKS label — so a board with nobody flagged had no
  // answer at all except a telephone.
  //
  // ⚠️ BEING ASKABLE IS NOT BEING ON THE ROUNDS, and the flag is the only thing
  // that says which. Requiring it to be ASKED would mean an AIC had to label a
  // colleague before ringing them — the friction the 2026-09-11 request was
  // about — so membership here is unconditional. But the flag itself is READ,
  // not hardcoded off: owner, 2026-09-13, seeing four anaesthesiologists under
  // the GIVING BREAKS heading — "the doctors are always availables to gives
  // breaks, dont put them in this list unles s we ask thme to be doing that."
  // Asking them to do the rounds is setting the flag, and it has to mean the
  // same thing for a doctor as for a CRNA; a hardcoded `false` made the
  // sentence "unless we ask them" unsayable.
  //
  // Tagged `isDoctor` so every surface can SAY it is offering an MD. The board,
  // the timeline card and the phone all read this one list, and a name with no
  // role beside it is how somebody picks a supervisor thinking they picked a
  // break CRNA.
  const doctorIds = new Set();
  const doctors = [];
  for (const d of roster?.supervisors || []) {
    if (!d || !d.id || departed[d.id] || doctorIds.has(d.id)) continue;
    doctorIds.add(d.id);
    // ⚠️ "MD", AND NEVER THE ENTRY'S OWN ROLE FIELD. Three of the four
    // supervisors on the live board carry NO `role` and NO `subRole` at all
    // (only the newest, written by a different path, has one), so a renderer
    // reading the entry fell through its `|| "crna"` default and labelled
    // Jessica Huang, Gonzalo Martinez and Leigh Tanner — all anaesthesiologists
    // — as CRNAs. Reported live on 2026-09-11: "Giving brakes, all are doctors,
    // no crna." This list is `roster.supervisors`; being IN it is what makes
    // somebody a doctor, and that is structural, not a field that may be blank.
    doctors.push({
      ...d,
      isDoctor: true,
      roleLabel: "MD",
      givingBreaks: !!flagged[d.id],
      room: "",
      assigned: false,
    });
  }

  return [...crnas, ...doctors];
}
